<template>
    <div
        class="m-announcement d-flex gap-1"
        :class="[{ new: !announcement.read }, size]"
        :id="`announcement-${announcement.id}`"
    >
        <div
            class="m-announcement__date d-flex flex-md-column gap-0-5 text-center text-sm"
        >
            <span
                :class="{
                    'font-weight-bold': !announcement.read,
                    'font-weight-normal': announcement.read
                }"
                v-tooltip="{
                    content: !isActive ? date.format('DD-MM-YYYY, HH:mm') : null
                }"
            >
                {{ date.format('DD-MM-YYYY') }}
                <p v-if="isActive" class="d-none d-sm-block mb-0">
                    {{ date.format('HH:mm') }}
                </p>
            </span>
            <div
                v-if="showNewBadge && !announcement.read"
                v-text="'Nowe'"
                class="a-badge"
            />
        </div>
        <div
            class="m-announcement__middle"
            :class="{ 'cursor-pointer': !isActive }"
            @click="goToDetails"
        >
            <h2
                class="m-announcement__title h5"
                :class="{
                    'font-weight-bold': !announcement.read,
                    'font-weight-normal': announcement.read
                }"
                v-text="announcement.title"
            />
            <div class="m-announcement__desc">
                <div v-if="isActive" class="m-announcement__content">
                    <p v-html="announcement.content"></p>
                    <div
                        v-if="areAttachments"
                        class="m-announcement__attachments mt-5"
                    >
                        <h5 class="mb-3">
                            Załączniki ({{ announcement.attachments.length }}):
                        </h5>
                        <promotion-slider
                            :data="attachmentsMap"
                            :responsive="{
                                120: {
                                    items: 1
                                },
                                400: {
                                    items: 2
                                },
                                520: {
                                    items: 3
                                },
                                620: {
                                    items: 4
                                },
                                768: {
                                    items: 3
                                },
                                900: {
                                    items: 4
                                },
                                1100: {
                                    items: 5
                                },
                                1420: {
                                    items: 6
                                }
                            }"
                        />
                    </div>
                </div>
                <p
                    v-else
                    class="m-announcement__lead mb-0"
                    :class="{
                        'font-weight-bold': !announcement.read,
                        'font-weight-normal': announcement.read
                    }"
                    v-html="$options.filters.plainText(announcement.content)"
                />
            </div>
        </div>
        <div class="m-announcement__options text-right" v-if="showOptions">
            <a-icon
                v-if="announcement.read"
                icon="MarkAsUnread"
                width="25px"
                height="25px"
                class="text-primary cursor-pointer"
                @click.native="markAsUnread(announcement.id)"
                v-tooltip="{
                    content: 'Oznacz jako nieprzeczytane'
                }"
            />
            <a-icon
                v-if="areAttachments"
                class="text-primary"
                icon="Attachment"
                width="25px"
                height="25px"
                v-tooltip="
                    'W ogłoszeniu znajdziesz załączniki, z którymi warto się zapoznać'
                "
            />
            <button
                @click="toggleFavorites(announcement.id)"
                class="c-promos-like btn btn-link"
                :class="{ 'is-active': announcement.favourite }"
            >
                <a-icon width="25px" height="25px" icon="Heart" />
            </button>
        </div>
        <div
            v-if="showMoreButton"
            class="m-announcement__more d-flex align-items-center justify-content-end"
        >
            <a-icon
                class="cursor-pointer text-primary"
                icon="ArrowRight"
                @click.native="goToDetails"
            />
        </div>
    </div>
</template>

<script>
import PromotionSlider from '@/components/promotions/PromotionSlider'
export default {
    name: 'AnnouncementPreview',
    components: {
        PromotionSlider
    },
    props: {
        announcement: {
            type: Object,
            required: true
        },
        activeId: {
            type: Number,
            required: false
        },
        showNewBadge: {
            type: Boolean,
            default: false
        },
        showMoreButton: {
            type: Boolean,
            default: false
        },
        showOptions: {
            type: Boolean,
            default: false
        },
        size: {
            type: String,
            default: 'normal'
        },
        clickable: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        areAttachments() {
            return this.announcement.attachments?.length > 0
        },
        attachmentsMap() {
            return this.announcement.attachments.map(el => {
                return {
                    name: el.name,
                    fileName: el.fileName,
                    urls: {
                        original: el.link
                    }
                }
            })
        },
        date() {
            return this.$moment(
                this.announcement.publicationDate,
                'DD-MM-YYYY HH:mm'
            )
        },
        isActive() {
            return this.activeId === this.announcement.id
        }
    },
    methods: {
        goToDetails() {
            if (!this.clickable) {
                return
            }

            this.$emit('setActiveId', this.announcement.id)
            const route = this.$route
            const expireDateValue = this.$moment.utc(
                this.announcement.expireDate,
                'DD-MM-YYYY HH:mm:ss'
            )

            const hashValue =
                Date.parse(expireDateValue) < Date.now()
                    ? '#archival'
                    : '#current'

            const queryParamId = new URLSearchParams(
                window.location.search
            ).get('id')

            if (
                route.name === 'announcements' &&
                (!queryParamId || +queryParamId !== this.announcement.id)
            ) {
                const p = this.$store.getters.getAnnouncementsPagination
                const page = Math.ceil((p.count + p.offset) / p.limit)
                history.pushState(
                    {},
                    null,
                    `?page=${page}&id=${this.announcement.id}${hashValue}`
                )
            } else if (route.name !== 'announcements') {
                this.$router.push({
                    name: 'announcements',
                    hash: hashValue,
                    query: { id: this.announcement.id }
                })
            }
        },
        toggleFavorites(announcementId) {
            this.$store
                .dispatch('toggleFavoriteAnnouncement', {
                    id: announcementId,
                    favourite: !this.announcement.favourite
                })
                .then(response => {
                    if (response.status === 200) {
                        this.announcement.favourite = !this.announcement
                            .favourite
                    }
                })
        },
        async markAsUnread(announcementId) {
            const res = await this.$store.dispatch('markAnnouncementAsRead', {
                id: announcementId,
                unread: true
            })
            if (res.status === 200) {
                this.$emit('setActiveId')
            }
        }
    }
}
</script>
